import axios, { AxiosError, type AxiosRequestConfig, type AxiosResponse, type Method } from 'axios';
import { StateService } from '@/services/state.service';

export interface AxiosServiceRequestParams {
	headers?: any;
	params?: any;
}

export interface AxiosServiceResponse {
	ok: boolean;
	status?: number;
	data: any;
	config: AxiosRequestConfig;
}

export type ErrorMapper = (err: AxiosError) => any;

export interface EndpointConfig {
	errorMapper?: ErrorMapper;

	/**
	 * << unique string >>
	 */
	key: string;

	/**
		'context' << prepend the value of the mock-context query string parameter to the key when making a mock-api request  >>
		'literal' << use only the key when making a mock-api request >>
		'none' << does not use the mock-api >>
		'function' << get the path from the mockPath function >>
	 */
	mock?: 'context' | 'literal' | 'none' | 'function';

	/**
	 * true | false << force the endpoint config entry to ALWAYS use the mock api.  Do not ever commit code with mockAlways: true >>
	 */
	mockAlways?: boolean;

	/**
	 * function to append a string to the end of the path (context and literal only)
	 */
	mockAppend?: (param: any) => string;

	/**
	 * << same as path, but is used when mock === 'function' and will give finer control over building a mock-api path >>
	 */
	mockPath: (param: any) => string;

	/**
	 * (<< any type or empty >>) => '<< literal path >>' OR `<< interpolated path >>` OR { << function body with return statement >> }
	 */
	path: (arg0: any) => string;
}

export interface AxiosServiceRequest {
	url: string;
	body: any;
}

/**
 * This class to be implemented by rest api classes
 */
export class AxiosServiceClass {
	// The endpointConfig is configured per inheriting class
	endpointConfig: EndpointConfig[] = [];

	// The endpointHost will default to APIM, but can be overrided by the inheriting class
	endpointHost = null;

	// The endpointPath is a path used by all inheriting API requests. It will be appended to the host.
	endpointPath = null;

	includeTimestampHeader = false;

	getHost() {
		return buildUrl(
			[
				this.endpointHost || StateService.getApimRoot(),
				this.endpointPath || ''
			]
		);
	}

	getConfig(params?: AxiosServiceRequestParams): AxiosServiceRequestParams {
		const config = {
			headers: {
				Pragma: 'no-cache',
				'ocp-apim-subscription-key': StateService.getApimKey()
			},
			params: params?.params || {}
		};

		const profileToken = StateService.getProfileToken();
		const ssoToken = StateService.getSSOToken();
		const authToken = StateService.getAuthToken();
		const jwe = StateService.getJwe();

		// TODO :: these assignments should be pulled out in the future to prevent
		//         sending auth tokens when not necessary
		if (profileToken) {
			Object.assign(config.headers, { 'X-LTF-PROFILE': profileToken });
		}
		if (ssoToken) {
			Object.assign(config.headers, { 'X-LTF-SSOID': ssoToken });
		}
		if (authToken) {
			Object.assign(config.headers, { 'X-LTF-CT': authToken });
		}
		if (jwe) {
			Object.assign(config.headers, { 'X-LTF-JWE': jwe });
		}
		if (this.includeTimestampHeader) {
			Object.assign(config.headers, { 'X-TIMESTAMP': new Date().toISOString() });
		}

		return config;
	}

	endpoint(key: string, param?: any): string {
		const config = this.endpointConfig.find(c => c.key === key);
		if (!config) throw new Error(`No endpoint found for key ${key}`);
		return getEndpoint(config, param, this.getHost());
	}

	validateOnly(config: AxiosServiceRequestParams) {
		Object.assign(config.headers, { 'X-Validate-Only': true });
		return config;
	}

	execute<T = any, R = AxiosResponse<T>>(method: Method, url: string | undefined, config: AxiosServiceRequestParams, body?: any): Promise<R> {
		const request: AxiosRequestConfig = {
			method: method,
			url: url,
			headers: config.headers,
			params: config.params
		};
		if (['put','post','patch', 'delete'].includes(method)) {
			request.data = body || {};
		}
		return axios.request(request);
	}

	get(url: string, config: AxiosServiceRequestParams, errorMapper?: ErrorMapper) {
		return this.request(this.execute('get', url, config), errorMapper);
	}

	gets(reqs: AxiosServiceRequest[], config: AxiosServiceRequestParams, errorMapper?: ErrorMapper) {
		const promises = [];
		for (const req of reqs) {
			promises.push(this.get(req.url, config, errorMapper));
		}
		return this.requests(promises);
	}

	delete(url: string, config: AxiosServiceRequestParams, errorMapper?: ErrorMapper) {
		return this.request(this.execute('delete', url, config), errorMapper);
	}

	deletes(reqs: AxiosServiceRequest[], config: AxiosServiceRequestParams, errorMapper?: ErrorMapper) {
		const promises = [];
		for (const req of reqs) {
			promises.push(this.delete(req.url, config, errorMapper));
		}
		return this.requests(promises);
	}

	post(url: string, body: any, config: AxiosServiceRequestParams, errorMapper?: ErrorMapper) {
		return this.request(this.execute('post', url, config, body), errorMapper);
	}

	posts(reqs: AxiosServiceRequest[], config: AxiosServiceRequestParams, errorMapper?: ErrorMapper) {
		const promises = [];
		for (const req of reqs) {
			promises.push(this.post(req.url, req.body, config, errorMapper));
		}
		return this.requests(promises);
	}

	put(url: string, body: any, config: AxiosServiceRequestParams, errorMapper?: ErrorMapper) {
		return this.request(this.execute('put', url, config, body), errorMapper);
	}

	puts(reqs: AxiosServiceRequest[], config: AxiosServiceRequestParams, errorMapper?: ErrorMapper) {
		const promises = [];
		for (const req of reqs) {
			promises.push(this.put(req.url, req.body, config, errorMapper));
		}
		return this.requests(promises);
	}

	request(promise: Promise<AxiosResponse>, errorMapper?: ErrorMapper): Promise<AxiosServiceResponse> {
		return promise
			.then((xhr) => {
				return {
					ok: true,
					data: xhr.data,
					config: xhr.config,
					headers: xhr.headers || {}
				} as AxiosServiceResponse;
			})
			.catch((err: AxiosError) => {
				return errorMapper ? errorMapper(err) : defaultErrorMapper(err);
			});
	}

	requests<T>(promises: (T | Promise<T>)[]): Promise<T[]> {
		return axios.all(promises);
	}
}

function getEndpoint(config: EndpointConfig, param: any, host: string) {
	if (StateService.isMockData() || config.mockAlways) {
		const context = StateService.getQuery('mockContext');
		if (!context && config.mock === 'context') config.mock = 'none';
		if (!config.mockAppend) config.mockAppend = () => '';

		switch (config.mock) {
			case 'function': {
				return `/mock-api/${config.mockPath(param)}`;
			}
			case 'context': {
				return `/mock-api/${context}-${config.key}${config.mockAppend(param)}`;
			}
			case 'literal': {
				return `/mock-api/${config.key}${config.mockAppend(param)}`;
			}
			case 'none':
			default: {
	            // If no mockContext is provided, it is by design that this will
	            // skip down and build a live endpoint
				break;
			}
		}
	}

	return buildUrl([
		host,
		config.path(param)
	]);
}

function buildUrl(fragments: string[]) {
	return fragments.join('/').replace(/([^:]\/)\/+/g, '$1');
}

function defaultErrorMapper(err: AxiosError): AxiosServiceResponse {
	const ret = {
		ok: false,
		status: 500,
		data: {},
		config: {}
	} as AxiosServiceResponse;

	if (err.response) {
		ret.status = err.response.status;
		if (typeof err.response.data === 'string') {
			ret.data = {
				message: err.response.data,
				errors: []
			};
		} else {
			ret.data = err.response.data;
		}
		if (err.response.config) {
			ret.config = err.response.config;
		}
	}
	return ret;
}
